import { useEffect, useState } from "react";

/**
 * ServiceFlow — compact 4-node engagement diagram.
 *
 * Sized to live inside a hero column (not a full-width centerpiece).
 * Same visual grammar as FlowDiagram: ring nodes, hairline connectors,
 * optional dashed return loop, blueprint stamp.
 */

export interface ServiceFlowNode {
  label: string;
  sub: string;
  emphasis?: boolean;
}

interface ServiceFlowProps {
  nodes: ServiceFlowNode[];
  /** Renders a dashed return path from last node to first (e.g. review loop). */
  loop?: boolean;
  /** Small mono caption rendered below the diagram, e.g. "SERVICE / 01". */
  stamp?: string;
  /** Caption next to the loop arc. */
  loopLabel?: string;
  className?: string;
}

const ServiceFlow = ({
  nodes,
  loop = false,
  stamp,
  loopLabel,
  className = "",
}: ServiceFlowProps) => {
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReveal(true), 120);
    return () => clearTimeout(t);
  }, []);

  const width = 420;
  const height = 260;
  const padX = 44;
  const rowY = loop ? 118 : 130;
  const count = nodes.length;
  const step = (width - padX * 2) / (count - 1);

  const points = nodes.map((n, i) => ({
    ...n,
    x: padX + step * i,
    y: rowY,
  }));

  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        role="img"
        aria-hidden="true"
      >
        {/* corner ticks — blueprint frame */}
        <g stroke="currentColor" className="text-primary/25" strokeWidth="0.6" fill="none">
          <path d="M6 6 L16 6 M6 6 L6 16" />
          <path d={`M${width - 6} 6 L${width - 16} 6 M${width - 6} 6 L${width - 6} 16`} />
          <path d={`M6 ${height - 6} L16 ${height - 6} M6 ${height - 6} L6 ${height - 16}`} />
          <path d={`M${width - 6} ${height - 6} L${width - 16} ${height - 6} M${width - 6} ${height - 6} L${width - 6} ${height - 16}`} />
        </g>

        {/* baseline rule */}
        <line
          x1={padX}
          y1={rowY}
          x2={width - padX}
          y2={rowY}
          stroke="currentColor"
          className="text-primary/20"
          strokeWidth="0.6"
          strokeDasharray={reveal ? "0" : "300"}
          style={{ transition: "stroke-dasharray 1.4s ease-out" }}
        />

        {/* connectors with arrow ticks */}
        {points.slice(0, -1).map((p, i) => {
          const next = points[i + 1];
          const mid = (p.x + next.x) / 2;
          return (
            <g key={`c-${i}`}>
              <path
                d={`M${mid - 4} ${rowY - 3} L${mid + 1} ${rowY} L${mid - 4} ${rowY + 3}`}
                stroke="currentColor"
                className="text-primary/45"
                strokeWidth="0.7"
                fill="none"
                style={{
                  opacity: reveal ? 1 : 0,
                  transition: `opacity 0.6s ease-out ${0.4 + i * 0.12}s`,
                }}
              />
            </g>
          );
        })}

        {/* loop arc */}
        {loop && (
          <g
            style={{
              opacity: reveal ? 1 : 0,
              transition: "opacity 0.8s ease-out 1.1s",
            }}
          >
            <path
              d={`M${points[count - 1].x} ${rowY + 8} Q${(points[0].x + points[count - 1].x) / 2} ${rowY + 70} ${points[0].x} ${rowY + 8}`}
              stroke="currentColor"
              className="text-secondary/45"
              strokeWidth="0.6"
              strokeDasharray="2 3"
              fill="none"
            />
            {loopLabel && (
              <text
                x={width / 2}
                y={rowY + 62}
                textAnchor="middle"
                className="fill-secondary/55 font-mono"
                style={{ fontSize: "7.5px", letterSpacing: "0.14em" }}
              >
                {loopLabel.toUpperCase()}
              </text>
            )}
          </g>
        )}

        {/* nodes */}
        {points.map((p, i) => (
          <g
            key={p.label}
            style={{
              opacity: reveal ? 1 : 0,
              transform: reveal ? "translateY(0)" : "translateY(4px)",
              transition: `opacity 0.6s ease-out ${0.15 + i * 0.1}s, transform 0.6s ease-out ${0.15 + i * 0.1}s`,
            }}
          >
            {/* outer ring for emphasis */}
            {p.emphasis && (
              <circle
                cx={p.x}
                cy={p.y}
                r="10"
                stroke="currentColor"
                className="text-secondary/40"
                strokeWidth="0.6"
                fill="none"
              />
            )}
            <circle
              cx={p.x}
              cy={p.y}
              r="6"
              stroke="currentColor"
              className={p.emphasis ? "text-secondary/70" : "text-primary/55"}
              strokeWidth="0.8"
              fill="hsl(var(--background))"
            />
            <circle
              cx={p.x}
              cy={p.y}
              r="1.8"
              className={p.emphasis ? "fill-secondary/80" : "fill-primary/70"}
            />

            {/* node index */}
            <text
              x={p.x}
              y={p.y - 18}
              textAnchor="middle"
              className="fill-foreground/35 font-mono"
              style={{ fontSize: "6.5px", letterSpacing: "0.16em" }}
            >
              {String(i + 1).padStart(2, "0")}
            </text>

            {/* node label */}
            <text
              x={p.x}
              y={p.y + 24}
              textAnchor="middle"
              className="fill-foreground/85 font-sans"
              style={{ fontSize: "10px", fontWeight: 500, letterSpacing: "-0.005em" }}
            >
              {p.label}
            </text>
            <text
              x={p.x}
              y={p.y + 36}
              textAnchor="middle"
              className="fill-muted-foreground/70 font-mono"
              style={{ fontSize: "6.5px", letterSpacing: "0.14em" }}
            >
              {p.sub.toUpperCase()}
            </text>
          </g>
        ))}
      </svg>

      {stamp && (
        <div className="mt-4 flex items-center gap-2 font-mono text-[9.5px] tracking-[0.18em] uppercase text-foreground/30">
          <span className="w-6 h-px bg-foreground/20" />
          {stamp}
        </div>
      )}
    </div>
  );
};

export default ServiceFlow;